import React from "react";
import {
  Box,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Skeleton,
  TextField,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

import useSSL from "../../../../hooks/super/useSSL";
import { formatDate } from "../../../../utils/format";
import { getComparator, stableSort } from "../../../../utils/tableSort";
import {
  TOKENS,
  Monogram,
  StatusPill,
  EmptyState,
} from "../../../../components/pages/_new/super/design";

const headCells = [
  { id: "organisation", label: "Organisation", sortable: true },
  { id: "host", label: "Host", sortable: true },
  { id: "root_domain", label: "Root Domain", sortable: true },
  { id: "validTo", label: "Expires", sortable: true },
  { id: "expired", label: "Status", sortable: true },
];

const HeaderCell = ({ children, sortable, id, orderBy, order, onSort, align }) => (
  <TableCell
    align={align || "left"}
    sx={{
      padding: "10px 14px",
      borderBottom: `1px solid ${TOKENS.border}`,
      background: TOKENS.bgSunken,
      color: TOKENS.fg2,
      fontSize: 11.5,
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: "0.06em",
    }}
  >
    {sortable ? (
      <TableSortLabel
        active={orderBy === id}
        direction={orderBy === id ? order : "asc"}
        onClick={() => onSort(id)}
        sx={{
          color: `${TOKENS.fg2} !important`,
          "& .MuiTableSortLabel-icon": { color: `${TOKENS.fg2} !important` },
        }}
      >
        {children}
      </TableSortLabel>
    ) : (
      children
    )}
  </TableCell>
);

const DataCell = ({ children, align }) => (
  <TableCell
    align={align || "left"}
    sx={{
      padding: "12px 14px",
      borderBottom: `1px solid ${TOKENS.borderSubtle}`,
      color: TOKENS.fg1,
      fontSize: 13.5,
    }}
  >
    {children}
  </TableCell>
);

const expiryCell = (validTo) => {
  if (!validTo) return <Typography sx={{ fontSize: 13, color: TOKENS.fg3 }}>—</Typography>;
  const d = new Date(validTo);
  const now = new Date();
  const days = Math.floor((d - now) / 86400000);
  if (d <= now) {
    return (
      <Typography
        component="span"
        sx={{
          fontSize: 12.5,
          color: TOKENS.dangerInk,
          background: TOKENS.dangerBg,
          border: `1px solid ${TOKENS.dangerBorder}`,
          borderRadius: 999,
          px: "10px",
          py: "2px",
          fontWeight: 600,
        }}
      >
        Expired · {formatDate(validTo)}
      </Typography>
    );
  }
  if (days < 30) {
    return (
      <Typography
        component="span"
        sx={{
          fontSize: 12.5,
          color: TOKENS.warningInk,
          background: TOKENS.warningBg,
          border: `1px solid ${TOKENS.warningBorder}`,
          borderRadius: 999,
          px: "10px",
          py: "2px",
          fontWeight: 600,
        }}
      >
        {days}d · {formatDate(validTo)}
      </Typography>
    );
  }
  return (
    <Typography sx={{ fontSize: 13, color: TOKENS.fg1 }}>{formatDate(validTo)}</Typography>
  );
};

function CertStatusList() {
  const { certStatus, certTotal, certFrom, certSize, getCertificateHistory } = useSSL();
  const [order, setOrder] = React.useState("desc");
  const [orderBy, setOrderBy] = React.useState("validTo");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [search, setSearch] = React.useState("");

  React.useEffect(() => {
    setPage(certFrom / certSize);
  }, [certFrom]); // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    setRowsPerPage(certSize);
  }, [certSize]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSort = (id) => {
    if (id === orderBy) setOrder(order === "asc" ? "desc" : "asc");
    else {
      setOrderBy(id);
      setOrder("asc");
    }
  };

  const filtered = React.useMemo(() => {
    const list = Array.isArray(certStatus) ? certStatus : [];
    if (!search) return list;
    const q = search.toLowerCase();
    return list.filter(
      (r) =>
        (r?.host || "").toLowerCase().includes(q) ||
        (r?.organisation || "").toLowerCase().includes(q) ||
        (r?.root_domain || "").toLowerCase().includes(q)
    );
  }, [certStatus, search]);
  const sorted = React.useMemo(
    () => stableSort(filtered, getComparator(order, orderBy)),
    [filtered, order, orderBy]
  );

  const isLoading = certStatus === null;
  const isEmpty = !isLoading && sorted.length === 0;
  const last = Math.max(0, Math.ceil(certTotal / rowsPerPage) - 1);

  return (
    <Box
      sx={{
        border: `1px solid ${TOKENS.border}`,
        borderRadius: `${TOKENS.radiusM}px`,
        background: TOKENS.bgSurface,
        boxShadow: TOKENS.shadowXs,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          p: "14px 16px",
          borderBottom: `1px solid ${TOKENS.borderSubtle}`,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexWrap: "wrap",
        }}
      >
        <TextField
          size="small"
          placeholder="Search by host, organisation, root domain..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: TOKENS.fg3 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            minWidth: 280,
            "& .MuiOutlinedInput-root": { borderRadius: "10px", background: TOKENS.bgSurface },
          }}
        />
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontSize: 12.5, color: TOKENS.fg3 }}>
          {isLoading ? "Loading..." : `${sorted.length} of ${certTotal}`}
        </Typography>
      </Box>

      <TableContainer>
        <Table sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow>
              {headCells.map((c) => (
                <HeaderCell
                  key={c.id}
                  id={c.id}
                  sortable={c.sortable}
                  orderBy={orderBy}
                  order={order}
                  onSort={handleSort}
                >
                  {c.label}
                </HeaderCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              Array.from({ length: rowsPerPage }).map((_, i) => (
                <TableRow key={`sk-${i}`}>
                  {headCells.map((__, j) => (
                    <DataCell key={`sk-${i}-${j}`}>
                      <Skeleton variant="rectangular" height={18} sx={{ borderRadius: 1 }} />
                    </DataCell>
                  ))}
                </TableRow>
              ))
            ) : isEmpty ? (
              <TableRow>
                <TableCell colSpan={headCells.length} sx={{ borderBottom: "none" }}>
                  <EmptyState
                    icon={<LockOutlinedIcon />}
                    title={search ? "No certificates match" : "No certificates yet"}
                    body={
                      search
                        ? "Try a different keyword."
                        : "Provisioned certificates will show up here."
                    }
                  />
                </TableCell>
              </TableRow>
            ) : (
              sorted.map((row, i) => {
                const isExpired =
                  row?.expired || (row?.validTo && new Date(row.validTo) <= new Date());
                return (
                  <TableRow
                    key={`${row?.host}-${i}`}
                    hover
                    sx={{ "&:hover": { background: TOKENS.bgSunken } }}
                  >
                    <DataCell>
                      <Stack direction="row" alignItems="center" spacing={1.25}>
                        <Monogram name={row?.organisation || "?"} size={32} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: 13.5,
                              fontWeight: 600,
                              color: TOKENS.fg1,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {row?.organisation || "—"}
                          </Typography>
                        </Box>
                      </Stack>
                    </DataCell>
                    <DataCell>
                      <Typography
                        sx={{
                          fontSize: 12.5,
                          color: TOKENS.fg1,
                          fontFamily:
                            "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                        }}
                      >
                        {row?.host || "—"}
                      </Typography>
                    </DataCell>
                    <DataCell>
                      <Typography
                        sx={{
                          fontSize: 12.5,
                          color: TOKENS.fg2,
                          fontFamily:
                            "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                        }}
                      >
                        {row?.root_domain || "—"}
                      </Typography>
                    </DataCell>
                    <DataCell>{expiryCell(row?.validTo)}</DataCell>
                    <DataCell>
                      <StatusPill status={isExpired ? "Expired" : "Active"} />
                    </DataCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {!isLoading && certTotal > 0 ? (
        <Box
          sx={{
            p: "10px 16px",
            borderTop: `1px solid ${TOKENS.borderSubtle}`,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <Typography sx={{ fontSize: 12.5, color: TOKENS.fg2 }}>
            Page {page + 1} of {Math.max(1, last + 1)}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Typography
            component="button"
            type="button"
            onClick={() => {
              if (page > 0) {
                setPage(page - 1);
                getCertificateHistory(rowsPerPage, rowsPerPage * (page - 1), false);
              }
            }}
            disabled={page === 0}
            sx={{
              border: `1px solid ${TOKENS.border}`,
              borderRadius: `${TOKENS.radiusXs}px`,
              background: TOKENS.bgSurface,
              color: page === 0 ? TOKENS.fg3 : TOKENS.fg1,
              fontSize: 12.5,
              fontWeight: 600,
              px: 1.5,
              py: 0.5,
              cursor: page === 0 ? "not-allowed" : "pointer",
            }}
          >
            Prev
          </Typography>
          <Typography
            component="button"
            type="button"
            onClick={() => {
              if (page < last) {
                setPage(page + 1);
                getCertificateHistory(rowsPerPage, rowsPerPage * (page + 1), false);
              }
            }}
            disabled={page >= last}
            sx={{
              border: `1px solid ${TOKENS.border}`,
              borderRadius: `${TOKENS.radiusXs}px`,
              background: TOKENS.bgSurface,
              color: page >= last ? TOKENS.fg3 : TOKENS.fg1,
              fontSize: 12.5,
              fontWeight: 600,
              px: 1.5,
              py: 0.5,
              cursor: page >= last ? "not-allowed" : "pointer",
            }}
          >
            Next
          </Typography>
          <Box
            component="select"
            value={rowsPerPage}
            onChange={(e) => {
              const v = parseInt(e.target.value, 10);
              setRowsPerPage(v);
              setPage(0);
              getCertificateHistory(v, 0, false);
            }}
            sx={{
              border: `1px solid ${TOKENS.border}`,
              borderRadius: `${TOKENS.radiusXs}px`,
              background: TOKENS.bgSurface,
              color: TOKENS.fg1,
              fontSize: 12.5,
              px: 1,
              py: 0.5,
            }}
          >
            <option value={5}>5 / page</option>
            <option value={10}>10 / page</option>
            <option value={25}>25 / page</option>
          </Box>
        </Box>
      ) : null}
    </Box>
  );
}

export default CertStatusList;
